import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {Filter, Sort} from 'search/SearchConstants';
import ItemSearchInput from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import {BLUE, BLUE_DARKER, GRAY_DARK, GRAY_LIGHTER} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ChipSearchInputSearch} from 'ui-library/inputs/ChipSearchInput';

export interface EditableSearchItem {
  id: string;
  name: string;
}

export interface EditableSearchListProps {
  isLoading: boolean;
  renderWhileLoading?: () => JSX.Element;
  selectedItems: Array<EditableSearchItem>;
  onSelectItem: (item: EditableSearchItem, isLinked: boolean) => void;
  doSearch?: ChipSearchInputSearch;
  searchUrl: string;
  filters?: Array<Filter>;
  sorts?: Array<Sort>;
  placeholderSearchText: string;
  emptyListMessage: string;
}

/**
 * Renders an editable search list component.
 *
 * @template TSearchResponse - The type of the search response.
 * @param {Object} props - The component props.
 * @param {boolean} props.isLoading - Indicates whether the component is in a loading state.
 * @param {() => JSX.Element} [props.renderWhileLoading] - Optional callback function to render content while loading.
 * @param {Array<EditableSearchItem>} props.selectedItems - The array of selected search items.
 * @param {(item: EditableSearchItem, isLinked: boolean) => void} props.onSelectItem - Callback function when an item is selected.
 * @param {ChipSearchInputSearch} [props.doSearch] - Optional search function.
 * @param {string} props.searchUrl - The URL for searching.
 * @param {Array<Filter>} [props.filters] - Optional array of filters.
 * @param {Array<Sort>} [props.sorts] - Optional array of sorts.
 * @param {string} props.placeholderSearchText - The placeholder text for the search input.
 * @param {string} props.emptyListMessage - The text if selectedItems is empty.
 * @returns {JSX.Element} The rendered editable search list component.
 */
export default function EditableSearchList<TSearchResponse = unknown>({
  isLoading,
  renderWhileLoading = () => <CenteredHorizontalThrobber />,
  selectedItems,
  onSelectItem,
  doSearch,
  searchUrl,
  filters,
  sorts,
  placeholderSearchText,
  emptyListMessage,
}: EditableSearchListProps): JSX.Element {
  const [isEditing, setIsEditing] = useState(false);

  if (isLoading) {
    return renderWhileLoading();
  } else if (isEditing) {
    return (
      <RenderEditableSearchListEditMode<TSearchResponse>
        selectedItems={selectedItems}
        onSelectItem={onSelectItem}
        doSearch={doSearch}
        searchUrl={searchUrl}
        filters={filters}
        sorts={sorts}
        placeholderSearchText={placeholderSearchText}
        exitEditing={() => {
          setIsEditing(false);
        }}
      />
    );
  } else {
    return (
      <RenderEditableSearchListDefault
        selectedItems={selectedItems}
        emptyListMessage={emptyListMessage}
        enterEditing={() => {
          setIsEditing(true);
        }}
      />
    );
  }
}

function RenderEditableSearchListEditMode<TSearchResponse>({
  selectedItems,
  onSelectItem,
  doSearch,
  searchUrl,
  filters,
  sorts,
  placeholderSearchText,
  exitEditing,
}: {
  selectedItems: Array<EditableSearchItem>;
  onSelectItem: (item: EditableSearchItem, isLinked: boolean) => void;
  doSearch?: ChipSearchInputSearch;
  searchUrl: string;
  filters?: Array<Filter>;
  sorts?: Array<Sort>;
  placeholderSearchText: string;
  exitEditing: () => void;
}): JSX.Element {
  const [isHoveringOnSubmit, submitHoverProps] = useHoverState();

  const searchFetchParams = {
    groupBy: null,
    limit: 10,
    nextToken: null,
    offset: 0,
    ...{filters, sorts},
  };

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.editWrapper)}>
        <ItemSearchInput<EditableSearchItem, TSearchResponse>
          chipSize={ChipSize.MINI}
          itemKey="data"
          onChange={() => {}}
          onItemChange={onSelectItem}
          placeholder={placeholderSearchText}
          doSearch={doSearch}
          searchURL={searchUrl}
          fetchParams={searchFetchParams}
          selectedItems={selectedItems}
          shouldAllowMultiple={true}
          hideSelectedResults={true}
        />
      </div>
      <BenchlingIcon
        className={css(
          styles.icon,
          styles.formIcon,
          isHoveringOnSubmit ? styles.hoveredSubmitIcon : styles.unhoveredSubmitIcon
        )}
        icon="check"
        onClick={() => {
          exitEditing();
        }}
        {...submitHoverProps}
      />
    </div>
  );
}

function RenderEditableSearchListDefault({
  selectedItems,
  emptyListMessage = 'Nothing selected',
  enterEditing,
}: {
  selectedItems: Array<EditableSearchItem>;
  emptyListMessage?: string;
  enterEditing: () => void;
}): JSX.Element {
  const [isHovering, hoverProps] = useHoverState();

  return (
    <div
      className={css(styles.wrapper, styles.defaultWrapper, isHovering ? styles.hover : null)}
      {...hoverProps}
      onClick={() => {
        enterEditing();
      }}
    >
      <div className={css(styles.defaultList)}>
        {selectedItems.length > 0 ? (
          <ChipList
            itemIds={selectedItems.map((item) => item.id)}
            isVertical={false}
            chipSize={ChipSize.MINI}
          />
        ) : (
          <div className={css(styles.emptyList)}>{emptyListMessage}</div>
        )}
      </div>
      {isHovering && <BenchlingIcon icon="edit" className={css(styles.icon, styles.editIcon)} />}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
  },
  editWrapper: {
    flex: '1 0 auto',
  },
  defaultWrapper: {
    marginLeft: -3,
  },
  defaultList: {
    minHeight: 24,
    margin: 'auto 3px auto 3px',
  },
  emptyList: {
    paddingTop: 3,
  },
  hover: {
    backgroundColor: GRAY_LIGHTER,
  },
  icon: {
    display: 'inline-block',
    margin: 'auto 10px auto 5px',
    cursor: 'pointer',
  },
  editIcon: {
    alignSelf: 'flex-end',
    color: GRAY_DARK,
  },
  formIcon: {
    alignSelf: 'flex-end',
    marginLeft: '10px',
  },
  unhoveredSubmitIcon: {
    color: BLUE,
  },
  hoveredSubmitIcon: {
    color: BLUE_DARKER,
  },
});
